import React, { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Edit2, Trash2, Save, XCircle } from "lucide-react";

/**
 * Ward manager for Hospital Admin.
 * - Add a new ward with a name, prefix, and initial bed count.
 * - Rename an existing ward.
 * - Adjust its bed count up/down (down only removes vacant trailing beds).
 * - Delete a ward (only when no beds are occupied).
 */
export function WardManagerDialog({ bedsData, onClose, onChanged }) {
  const wards = useMemo(() => Object.keys(bedsData?.wards || {}).sort(), [bedsData]);
  const [editing, setEditing] = useState(null); // ward name currently being edited
  const [renameTo, setRenameTo] = useState("");
  const [targetBeds, setTargetBeds] = useState("");
  const [prefixNew, setPrefixNew] = useState("");
  const [saving, setSaving] = useState(false);

  // Add-ward form state
  const [showAdd, setShowAdd] = useState(false);
  const [newWard, setNewWard] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [newBeds, setNewBeds] = useState("10");

  const beginEdit = (w) => {
    setEditing(w);
    setRenameTo(w);
    setTargetBeds(String(bedsData.wards[w]?.total ?? ""));
    // Derive prefix from first bed_no in ward
    const firstBed = (bedsData.beds || []).find((b) => b.ward === w);
    setPrefixNew(firstBed ? String(firstBed.bed_no).split("-")[0] : "");
  };

  const cancelEdit = () => { setEditing(null); setRenameTo(""); setTargetBeds(""); setPrefixNew(""); };

  const saveEdit = async (w) => {
    const stats = bedsData.wards[w] || { total: 0, occupied: 0 };
    const currentTotal = stats.total || 0;
    const newTotal = Number(targetBeds);
    if (!Number.isFinite(newTotal) || newTotal < 0 || newTotal > 500) { toast.error("Bed count must be 0–500"); return; }
    try {
      setSaving(true);
      // 1) Rename if needed
      let effectiveName = w;
      if (renameTo.trim() && renameTo.trim() !== w) {
        await api.patch(`/wards/${encodeURIComponent(w)}`, { new_name: renameTo.trim() });
        effectiveName = renameTo.trim();
      }
      // 2) Adjust bed count if changed
      if (newTotal !== currentTotal) {
        await api.post(`/wards/${encodeURIComponent(effectiveName)}/beds`, {
          target_beds: newTotal,
          prefix: prefixNew.trim().toUpperCase() || null,
        });
      }
      toast.success(`Saved ${effectiveName}`);
      cancelEdit();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save ward");
    } finally { setSaving(false); }
  };

  const deleteWard = async (w) => {
    if (!window.confirm(`Delete ward "${w}" and all its beds? This cannot be undone.`)) return;
    try {
      await api.delete(`/wards/${encodeURIComponent(w)}`);
      toast.success(`${w} removed`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot delete ward");
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!newWard.trim() || !newPrefix.trim()) { toast.error("Ward name and prefix required"); return; }
    const beds = Number(newBeds);
    if (!Number.isFinite(beds) || beds < 1 || beds > 200) { toast.error("Beds must be 1–200"); return; }
    try {
      setSaving(true);
      await api.post("/wards", { ward: newWard.trim(), prefix: newPrefix.trim().toUpperCase(), beds });
      toast.success(`Ward ${newWard.trim()} created`);
      setShowAdd(false); setNewWard(""); setNewPrefix(""); setNewBeds("10");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add ward");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl shadow-xl border border-slate-200 max-h-[92vh] overflow-hidden flex flex-col" data-testid="ward-manager-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold flex items-center gap-2">Manage wards &amp; beds</div>
            <div className="text-xs text-slate-500 mt-0.5">Rename wards, adjust bed counts, or add a new ward. Occupied beds cannot be removed.</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {wards.length === 0 && !showAdd && (
            <div className="text-sm text-slate-500 py-4">No wards yet. Click “Add ward” to create one.</div>
          )}

          {wards.map((w) => {
            const stats = bedsData.wards[w] || { total: 0, occupied: 0 };
            const isEditing = editing === w;
            return (
              <div key={w} className="rounded-lg border border-slate-200 p-3" data-testid={`ward-manager-row-${w}`}>
                {!isEditing ? (
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{w}</div>
                      <div className="text-[11px] text-slate-500">{stats.total} bed{stats.total === 1 ? "" : "s"} · {stats.occupied} occupied · {stats.vacant || 0} free</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`ward-edit-${w}`} onClick={() => beginEdit(w)} className="h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                      <button
                        data-testid={`ward-delete-${w}`}
                        onClick={() => deleteWard(w)}
                        disabled={stats.occupied > 0}
                        title={stats.occupied > 0 ? "Cannot delete: beds are occupied" : ""}
                        className="h-8 px-3 rounded-lg text-xs font-medium border border-red-200 text-[#B71C1C] hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent flex items-center gap-1.5"
                      ><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium">Ward name</label>
                      <input data-testid={`ward-rename-${w}`} value={renameTo} onChange={(e) => setRenameTo(e.target.value)} className="w-full h-9 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium">Bed count</label>
                      <input data-testid={`ward-count-${w}`} type="number" min="0" max="500" value={targetBeds} onChange={(e) => setTargetBeds(e.target.value)} className="w-full h-9 mt-1 px-3 rounded-lg border border-slate-200 text-sm tabular" />
                      <div className="text-[10px] text-slate-400 mt-0.5">Currently {stats.total} · {stats.occupied} occupied. Shrinking removes vacant beds first.</div>
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 font-medium">Bed prefix (for new beds)</label>
                      <input data-testid={`ward-prefix-${w}`} value={prefixNew} onChange={(e) => setPrefixNew(e.target.value.toUpperCase())} placeholder="e.g. G, ICU" className="w-full h-9 mt-1 px-3 rounded-lg border border-slate-200 text-sm uppercase" />
                    </div>
                    <div className="md:col-span-3 flex items-center justify-end gap-2 pt-1">
                      <button data-testid={`ward-cancel-${w}`} type="button" onClick={cancelEdit} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100 flex items-center gap-1.5"><XCircle className="w-4 h-4" /> Cancel</button>
                      <button data-testid={`ward-save-${w}`} type="button" disabled={saving} onClick={() => saveEdit(w)} className="h-9 px-4 rounded-lg text-sm font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] disabled:opacity-60 flex items-center gap-1.5"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {showAdd ? (
            <form onSubmit={submitAdd} className="rounded-lg border-2 border-dashed border-[#1976D2]/40 p-3 space-y-3 bg-blue-50/30" data-testid="ward-add-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 font-medium">Ward name *</label>
                  <input data-testid="ward-new-name" value={newWard} onChange={(e) => setNewWard(e.target.value)} placeholder="e.g. Oncology" className="w-full h-9 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 font-medium">Bed prefix *</label>
                  <input data-testid="ward-new-prefix" value={newPrefix} onChange={(e) => setNewPrefix(e.target.value.toUpperCase())} placeholder="e.g. ONC" className="w-full h-9 mt-1 px-3 rounded-lg border border-slate-200 text-sm uppercase" />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 font-medium">Initial beds *</label>
                  <input data-testid="ward-new-beds" type="number" min="1" max="200" value={newBeds} onChange={(e) => setNewBeds(e.target.value)} className="w-full h-9 mt-1 px-3 rounded-lg border border-slate-200 text-sm tabular" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowAdd(false)} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
                <button data-testid="ward-add-submit" type="submit" disabled={saving} className="h-9 px-4 rounded-lg text-sm font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] disabled:opacity-60 flex items-center gap-1.5"><Plus className="w-4 h-4" /> {saving ? "Creating…" : "Create ward"}</button>
              </div>
            </form>
          ) : (
            <button data-testid="ward-add-open" onClick={() => setShowAdd(true)} className="w-full h-10 rounded-lg text-sm font-medium border-2 border-dashed border-slate-300 text-slate-600 hover:border-[#1976D2] hover:text-[#1565C0] flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add ward</button>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="h-9 px-4 rounded-lg text-sm text-slate-700 hover:bg-slate-100">Done</button>
        </div>
      </div>
    </div>
  );
}
